/**
 * QAQC Chart Data Transformation Utilities
 * Transforms raw API data into recharts-compatible format
 */

import { formatChartAxisDate } from './dateFilters';

/**
 * QAQC Color Palette for consistent chart styling
 */
export const COLORS = {
  pass: '#10b981',      // Green
  warning: '#f59e0b',   // Yellow/Orange
  fail: '#ef4444',      // Red
  certified: '#06b6d4', // Cyan
  measured: '#3b82f6',  // Blue
  mean: '#06b6d4',      // Cyan (for mean lines)
  value: '#3b82f6',     // Blue (for value lines)
  grid: '#374151',      // Dark grid
  text: '#9ca3af'       // Gray text
};

/**
 * Field-duplicate rows from optimized qaqc.vw_field_duplicates_qc may omit
 * within_rpd_tolerance_dynamic; fall back to static so Pass/Fail logic stays correct.
 */
export const effectiveWithinRpdDynamic = (item) =>
  item.within_rpd_tolerance_dynamic ?? item.within_rpd_tolerance_static;

/**
 * Compare received_date ascending; invalid/missing dates sort last.
 * Same-date / missing-date rows cluster by workorder, then sequence.
 */
const sequenceValue = (item, preferredKey = null) => {
  const keys = preferredKey
    ? [preferredKey, 'sample_seq_num', 'sample_sequence', 'dup_sample_sequence']
    : ['sample_seq_num', 'sample_sequence', 'dup_sample_sequence'];
  const seen = new Set();
  for (const key of keys) {
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (item[key] == null || item[key] === '') continue;
    const n = parseInt(item[key], 10);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const compareByReceivedDate = (a, b, tiebreakerKey = null) => {
  const ta = a.received_date != null ? new Date(a.received_date).getTime() : NaN;
  const tb = b.received_date != null ? new Date(b.received_date).getTime() : NaN;
  const aValid = Number.isFinite(ta);
  const bValid = Number.isFinite(tb);
  if (aValid && bValid && ta !== tb) return ta - tb;
  if (aValid && !bValid) return -1;
  if (!aValid && bValid) return 1;

  const wa = String(a.workorder_id || a.orig_workorder_id || '');
  const wb = String(b.workorder_id || b.orig_workorder_id || '');
  if (wa !== wb) return wa.localeCompare(wb);

  const na = sequenceValue(a, tiebreakerKey);
  const nb = sequenceValue(b, tiebreakerKey);
  if (na !== nb) return na - nb;
  return 0;
};

/**
 * Transform control standards data for z-score chart
 * @param {Array} data - Raw control standards data
 * @param {string} element - Element to filter (Au, Ag, Cu)
 * @param {string} crmCode - CRM code to filter (optional)
 * @returns {Array} Chart-ready data
 */
export const transformControlStandardsForChart = (data, element, crmCode = null) => {
  if (!data || data.length === 0) return [];
  
  // Filter by element and CRM code
  let filtered = data.filter(item => item.element === element);
  
  if (crmCode && crmCode !== 'all') {
    filtered = filtered.filter(item => item.crm_code === crmCode);
  }
  
  // Only include items with certified values (actual CRMs, not blanks)
  filtered = filtered.filter(item => 
    item.certified_value !== null && 
    item.certified_value !== undefined &&
    item.z_score !== null &&
    item.z_score !== undefined
  );
  
  // Sort by received date, then sample sequence
  filtered.sort((a, b) => compareByReceivedDate(a, b, 'sample_sequence'));
  
  // Transform to chart format
  return filtered.map((item, index) => ({
    // Display fields
    sampleId: item.sample_id || item.sample_number,
    sampleNumber: item.sample_number,
    workorderId: item.workorder_id,
    sequence: item.sample_sequence,
    holeId: item.hole_id,
    crmCode: item.crm_code,
    receivedDate: item.received_date,
    
    // Chart data
    index: index,
    zScore: parseFloat(item.z_score) || 0,
    bestValue: parseFloat(item.best_value) || 0,
    certifiedValue: parseFloat(item.certified_value) || 0,
    stdDev: parseFloat(item.standard_deviation) || 0,
    diffFromCert: parseFloat(item.diff_from_cert) || 0,
    
    // Status flags
    within2sd: item.within_2sd,
    within3sd: item.within_3sd,
    
    // Determine status for coloring
    status: !item.within_3sd ? 'fail' : 
            !item.within_2sd ? 'warning' : 'pass',
    
    // Raw data for tooltips
    raw: item
  }));
};

/**
 * Transform blank failures data for trend chart
 * @param {Array} data - Raw blank failures data
 * @param {string} element - Element (Ag, Au, Cu)
 * @returns {Array} Chart-ready data
 */
export const transformBlankDataForChart = (data, element) => {
  if (!data || data.length === 0) return [];
  
  // Sort by workorder and sequence
  const sorted = [...data].sort((a, b) => {
    if (a.workorder_id !== b.workorder_id) {
      return a.workorder_id.localeCompare(b.workorder_id);
    }
    return parseInt(a.sample_sequence) - parseInt(b.sample_sequence);
  });
  
  // Get the value key based on element
  const valueKey = element === 'Ag' ? 'ag_value' : 
                   element === 'Au' ? 'au_value' : 'cu_value';
  const meanKey = element === 'Ag' ? 'mean_ag' : 
                  element === 'Au' ? 'mean_au' : 'mean_cu';
  const stddevKey = element === 'Ag' ? 'stddev_ag' : 
                    element === 'Au' ? 'stddev_au' : 'stddev_cu';
  
  return sorted.map((item, index) => {
    const value = parseFloat(item[valueKey]) || 0;
    const mean = parseFloat(item[meanKey]) || 0;
    const stddev = parseFloat(item[stddevKey]) || 0;
    const zScore = parseFloat(item.z_score) || 0;
    
    return {
      index: index,
      sampleNumber: item.sample_number,
      workorderId: item.workorder_id,
      sequence: item.sample_sequence,
      holeId: item.hole_id,
      
      // Chart data
      value: value,
      mean: mean,
      stddev: stddev,
      zScore: zScore,
      carryover: parseFloat(item.crushing_carryover_pct) || 0,
      
      // Control limits
      upperWarning: mean + (2 * stddev),
      lowerWarning: mean - (2 * stddev),
      upperControl: mean + (3 * stddev),
      lowerControl: mean - (3 * stddev),
      
      // Status
      exceedsWarning: item.exceeds_warning_limit,
      exceedsControl: item.exceeds_control_limit,
      qcStatus: item.qc_status,
      
      status: item.qc_status?.toUpperCase().startsWith('FAIL') ? 'fail' :
              item.exceeds_warning_limit ? 'warning' : 'pass',
      
      raw: item
    };
  });
};

/**
 * Transform field duplicates data for scatter plot
 * @param {Array} data - Raw field duplicates data
 * @param {string} element - Element to filter
 * @returns {Array} Chart-ready data
 */
export const transformFieldDuplicatesForChart = (data, element) => {
  if (!data || data.length === 0) return [];
  
  // Filter by element
  const filtered = data.filter(item => item.element === element);
  
  return filtered.map((item, index) => ({
    index: index,
    parentSample: item.parent_sample,
    origSampleNumber: item.orig_sample_number,
    dupSampleNumber: item.dup_sample_number,
    workorderId: item.orig_workorder_id,
    holeId: item.hole_id,
    depth: `${item.from_depth}-${item.to_depth}`,
    
    // Chart data
    origValue: parseFloat(item.orig_value) || 0,
    dupValue: parseFloat(item.dup_value) || 0,
    meanValue: parseFloat(item.mean_value) || 0,
    rpdPct: parseFloat(item.rpd_pct) || 0,
    gradeBin: item.grade_bin,
    
    // Status
    withinStatic: item.within_rpd_tolerance_static,
    withinDynamic: effectiveWithinRpdDynamic(item),
    status: !effectiveWithinRpdDynamic(item) ? 'fail' :
            !item.within_rpd_tolerance_static ? 'warning' : 'pass',
    
    raw: item
  }));
};

/**
 * Get unique CRM codes from control standards data
 * @param {Array} data - Control standards data
 * @returns {Array} Sorted array of unique CRM codes
 */
export const getUniqueCRMCodes = (data) => {
  if (!data || data.length === 0) return [];
  
  const crmCodes = new Set();
  data.forEach(item => {
    if (item.crm_code && item.certified_value !== null) {
      crmCodes.add(item.crm_code);
    }
  });
  
  return Array.from(crmCodes).sort();
};

/**
 * Calculate summary statistics for a dataset
 * @param {Array} data - Numeric array
 * @returns {Object} Statistics (mean, stddev, min, max)
 */
export const calculateStats = (data) => {
  if (!data || data.length === 0) {
    return { mean: 0, stddev: 0, min: 0, max: 0, count: 0 };
  }
  
  const values = data.filter(v => typeof v === 'number' && !isNaN(v));
  const count = values.length;
  
  if (count === 0) {
    return { mean: 0, stddev: 0, min: 0, max: 0, count: 0 };
  }
  
  const mean = values.reduce((sum, v) => sum + v, 0) / count;
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / count;
  const stddev = Math.sqrt(variance);
  const min = Math.min(...values);
  const max = Math.max(...values);
  
  return { mean, stddev, min, max, count };
};

/**
 * Calculate moving average
 * @param {Array} data - Array of numbers
 * @param {number} window - Window size
 * @returns {Array} Moving averages
 */
export const calculateMovingAverage = (data, window = 5) => {
  if (!data || data.length < window) return data;
  
  const result = [];
  for (let i = 0; i < data.length; i++) {
    if (i < window - 1) {
      result.push(null);
    } else {
      const slice = data.slice(i - window + 1, i + 1);
      const avg = slice.reduce((sum, v) => sum + v, 0) / window;
      result.push(avg);
    }
  }
  return result;
};

// ==================== BIAS/DRIFT ANALYSIS TRANSFORMATIONS ====================

/**
 * Transform standards drift data for drift chart
 * Includes EWMA, rolling stats, and run-rule flags
 * @param {Array} data - Raw standards drift data
 * @param {string} element - Element to filter
 * @param {string} crmCode - CRM code to filter (optional)
 * @returns {Array} Chart-ready data
 */
export const transformStandardsDriftForChart = (data, element, crmCode = null) => {
  if (!data || data.length === 0) return [];
  
  // Filter by element and CRM code
  let filtered = data.filter(item => item.element === element);
  
  if (crmCode && crmCode !== 'all') {
    filtered = filtered.filter(item => item.crm_code === crmCode);
  }
  
  // Sort by received_date, then workorder / sample sequence (null dates last)
  filtered.sort((a, b) => compareByReceivedDate(a, b, 'sample_seq_num'));
  
  // Transform to chart format
  return filtered.map((item, index) => {
    // Collect active run-rule flags
    const activeFlags = [];
    if (item.flag_shift_8_same_side) activeFlags.push('8 consecutive same side');
    if (item.flag_trend_6_increasing) activeFlags.push('6 increasing trend');
    if (item.flag_trend_6_decreasing) activeFlags.push('6 decreasing trend');
    if (item.flag_2_of_3_pos_2sd) activeFlags.push('2 of 3 > +2SD');
    if (item.flag_2_of_3_neg_2sd) activeFlags.push('2 of 3 < -2SD');
    if (item.flag_4_of_5_pos_1sd) activeFlags.push('4 of 5 > +1SD');
    if (item.flag_4_of_5_neg_1sd) activeFlags.push('4 of 5 < -1SD');
    
    return {
      index: index,
      sampleNumber: item.sample_number,
      sampleSeqNum: parseInt(item.sample_seq_num) || 0,
      workorderId: item.workorder_id,
      receivedDate: item.received_date,
      holeId: item.hole_id,
      crmCode: item.crm_code,
      labCode: item.lab_code,
      
      // Chart data
      zScore: parseFloat(item.z_score) || 0,
      ewmaZ: parseFloat(item.ewma_z) || null,
      rollingMeanZ20: parseFloat(item.rolling_mean_z20) || null,
      rollingSDZ20: parseFloat(item.rolling_sd_z20) || null,
      bestValue: parseFloat(item.best_value) || 0,
      certifiedValue: parseFloat(item.certified_value) || 0,
      
      // Status
      qcStatus: item.qc_status_calc,
      status: item.qc_status_calc === 'FAIL' ? 'fail' : 
              item.qc_status_calc === 'WARN' ? 'warning' : 'pass',
      
      // Run-rule flags
      hasFlags: activeFlags.length > 0,
      activeFlags: activeFlags,
      flagCount: activeFlags.length,
      
      raw: item
    };
  });
};

/**
 * Transform standards bias workorder data for heatmap
 * @param {Array} data - Raw standards bias workorder data
 * @param {string} element - Element to filter (optional)
 * @param {number} minNCrm - Minimum n_crm to include
 * @returns {Array} Chart-ready data
 */
export const transformStandardsBiasWorkorderForChart = (data, element = null, minNCrm = 0) => {
  if (!data || data.length === 0) return [];
  
  // Filter by element if specified
  let filtered = element ? data.filter(item => item.element === element) : data;
  
  // Filter by minimum n_crm
  filtered = filtered.filter(item => (parseInt(item.n_crm) || 0) >= minNCrm);
  
  return filtered.map(item => ({
    workorderId: item.workorder_id,
    element: item.element,
    crmCode: item.crm_code,
    labCode: item.lab_code,
    receivedDate: item.received_date,
    
    // Bias metrics
    nCrm: parseInt(item.n_crm) || 0,
    meanZ: parseFloat(item.mean_z) || 0,
    medianZ: parseFloat(item.median_z) || 0,
    sdZ: parseFloat(item.sd_z) || 0,
    meanAbsZ: parseFloat(item.mean_abs_z) || 0,
    pctWarn: parseFloat(item.pct_warn) || 0,
    pctFail: parseFloat(item.pct_fail) || 0,
    zSlopeVsSequence: parseFloat(item.z_slope_vs_sequence) || 0,
    
    // Determine status
    status: Math.abs(parseFloat(item.mean_z) || 0) >= 0.5 ? 'significant' : 'normal',
    
    raw: item
  }));
};

/**
 * Transform standards bias month data for monthly trend chart
 * @param {Array} data - Raw standards bias month data
 * @param {string} element - Element to filter (optional)
 * @param {string} crmCode - CRM code to filter (optional)
 * @returns {Array} Chart-ready data
 */
export const transformStandardsBiasMonthForChart = (data, element = null, crmCode = null) => {
  if (!data || data.length === 0) return [];
  
  // Filter by element and CRM code
  let filtered = data;
  if (element) filtered = filtered.filter(item => item.element === element);
  if (crmCode && crmCode !== 'all') filtered = filtered.filter(item => item.crm_code === crmCode);
  
  // Sort by month (normalize to YYYY-MM-DD so SQL timestamps sort correctly)
  filtered.sort((a, b) =>
    formatChartAxisDate(a.month).localeCompare(formatChartAxisDate(b.month))
  );
  
  return filtered.map((item, index) => ({
    index: index,
    month: formatChartAxisDate(item.month),
    element: item.element,
    crmCode: item.crm_code,
    labCode: item.lab_code,
    
    // Bias metrics
    nCrm: parseInt(item.n_crm) || 0,
    meanZ: parseFloat(item.mean_z) || 0,
    medianZ: parseFloat(item.median_z) || 0,
    sdZ: parseFloat(item.sd_z) || 0,
    meanAbsZ: parseFloat(item.mean_abs_z) || 0,
    pctWarn: parseFloat(item.pct_warn) || 0,
    pctFail: parseFloat(item.pct_fail) || 0,
    
    raw: item
  }));
};

/**
 * Transform blanks drift data for contamination timeline
 * @param {Array} data - Raw blanks drift data
 * @param {string} element - Element to filter
 * @returns {Array} Chart-ready data
 */
export const transformBlanksDriftForChart = (data, element) => {
  if (!data || data.length === 0) return [];
  
  // Filter by element
  const filtered = data.filter(item => item.element === element);
  
  // Sort by received_date and sample_seq_num (null dates last)
  const sorted = [...filtered].sort((a, b) =>
    compareByReceivedDate(a, b, 'sample_seq_num')
  );
  
  return sorted.map((item, index) => {
    const value = parseFloat(item.value) || 0;
    const mean = parseFloat(item.mean_value) || 0;
    const stddev = parseFloat(item.stddev_value) || 0;
    
    return {
      index: index,
      sampleNumber: item.sample_number,
      sampleSeqNum: parseInt(item.sample_seq_num) || 0,
      workorderId: item.workorder_id,
      receivedDate: item.received_date,
      holeId: item.hole_id,
      labCode: item.lab_code,
      
      // Chart data
      value: value,
      mean: mean,
      stddev: stddev,
      zScore: parseFloat(item.z_score) || 0,
      rollingMeanZ20: parseFloat(item.rolling_mean_z20) || null,
      carryoverPct: parseFloat(item.crushing_carryover_pct) || 0,
      
      // Control limits
      upperWarning: mean + (2 * stddev),
      lowerWarning: mean - (2 * stddev),
      upperControl: mean + (3 * stddev),
      lowerControl: mean - (3 * stddev),
      
      // Flags
      exceedsWarning: item.exceeds_warning_limit,
      exceedsControl: item.exceeds_control_limit,
      flagCarryoverGE10: item.flag_carryover_ge_10pct,
      flagCarryoverGE20: item.flag_carryover_ge_20pct,
      flagShift8: item.flag_shift_8_same_side,
      
      // Status
      qcStatus: item.qc_status,
      status: item.qc_status?.toUpperCase().startsWith('FAIL') ? 'fail' :
              item.exceeds_warning_limit ? 'warning' : 'pass',
      
      raw: item
    };
  });
};

/**
 * Transform duplicates drift data for RPD time series
 * @param {Array} data - Raw duplicates drift data
 * @param {string} element - Element to filter
 * @returns {Array} Chart-ready data
 */
export const transformDuplicatesDriftForChart = (data, element) => {
  if (!data || data.length === 0) return [];
  
  // Filter by element
  const filtered = data.filter(item => item.element === element);
  
  // Sort by received_date (null dates last); parent_sample as stable tiebreaker
  const sorted = [...filtered].sort((a, b) => {
    const byDate = compareByReceivedDate(a, b, null);
    if (byDate !== 0) return byDate;
    return String(a.parent_sample || a.orig_sample_number || '').localeCompare(
      String(b.parent_sample || b.orig_sample_number || '')
    );
  });
  
  return sorted.map((item, index) => ({
    index: index,
    parentSample: item.parent_sample,
    origSampleNumber: item.orig_sample_number,
    dupSampleNumber: item.dup_sample_number,
    workorderId: item.orig_workorder_id,
    receivedDate: item.received_date,
    holeId: item.hole_id,
    labCode: item.lab_code,
    
    // Chart data
    origValue: parseFloat(item.orig_value) || 0,
    dupValue: parseFloat(item.dup_value) || 0,
    meanValue: parseFloat(item.mean_value) || 0,
    rpdPct: parseFloat(item.rpd_pct) || 0,
    rollingMeanRPD50: parseFloat(item.rolling_mean_rpd50) || null,
    gradeBin: item.grade_bin,
    
    // Tolerance values
    binRPDP50: parseFloat(item.bin_rpd_p50) || 0,
    binRPDP90: parseFloat(item.bin_rpd_p90) || 0,
    binRPDP95: parseFloat(item.bin_rpd_p95) || 0,
    
    // Flags
    flag5FailsIn50: item.flag_5_fails_in_last_50,
    withinStatic: item.within_rpd_tolerance_static,
    withinDynamic: effectiveWithinRpdDynamic(item),
    
    // Status
    status: !effectiveWithinRpdDynamic(item) ? 'fail' :
            !item.within_rpd_tolerance_static ? 'warning' : 'pass',
    
    raw: item
  }));
};

/**
 * Get unique CRM codes from standards drift data
 * @param {Array} data - Standards drift data
 * @param {string} element - Element to filter
 * @returns {Array} Sorted array of unique CRM codes
 */
export const getUniqueCRMCodesFromDrift = (data, element) => {
  if (!data || data.length === 0) return [];
  
  const filtered = element ? data.filter(item => item.element === element) : data;
  const crmCodes = new Set();
  
  filtered.forEach(item => {
    if (item.crm_code) {
      crmCodes.add(item.crm_code);
    }
  });
  
  return Array.from(crmCodes).sort();
};

/**
 * Get color for heatmap cell based on mean_z value
 * @param {number} meanZ - Mean z-score value
 * @returns {string} Color hex code
 */
export const getHeatmapColor = (meanZ) => {
  // Gradient: blue (negative bias) -> white (0) -> red (positive bias)
  const absZ = Math.abs(meanZ);
  
  if (absZ < 0.3) return '#f9fafb'; // Near zero - white/light gray
  
  if (meanZ < 0) {
    // Negative bias - shades of blue
    if (absZ >= 1.0) return '#1e40af'; // Dark blue
    if (absZ >= 0.7) return '#3b82f6'; // Blue
    if (absZ >= 0.5) return '#60a5fa'; // Light blue
    return '#93c5fd'; // Very light blue
  } else {
    // Positive bias - shades of red
    if (absZ >= 1.0) return '#991b1b'; // Dark red
    if (absZ >= 0.7) return '#ef4444'; // Red
    if (absZ >= 0.5) return '#f87171'; // Light red
    return '#fca5a5'; // Very light red
  }
};
